'use client'

import { useEffect } from 'react'
import type { DependencyList } from 'react'

import throttle from 'lodash/throttle'

/**
 * 1. Excecute onScroll function with throttle
 * 2. Cancel function execution if component already unmounted
 *
 *
 * Usage example:
 *
 * useThrottledScroll(() => {
 *     let scrollTop = document.documentElement.scrollTop || 0
 *
 *     return (scrollEvent) => setScrollTop(document.documentElement.scrollTop)

 * }, [], {delay: 100})
 *
 *
 * options:
 * - containerRef: pass in ref.current if the scrolled container is not window
 * - delay: time before event is throttled
 **/

export function useThrottledScroll(
    wrapperFn: () => (evt: UIEvent) => void,
    dependencies: DependencyList = [],
    options: { delay?: number } = {},
) {
    useEffect(() => {
        let componentUnmounted = false
        const throttledFn = wrapperFn()
        const throttledHandleScroll = throttle((scrollEvent) => {
            if (!componentUnmounted) throttledFn(scrollEvent)
        }, options.delay || 150)

        window.addEventListener('scroll', throttledHandleScroll)
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll)
            componentUnmounted = true
        }
    }, [wrapperFn, options.delay, ...dependencies])
}
